import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SBSLogoMark from "./SBSLogoMark";

const BLOCKED_ACCESS_STATUSES = new Set(["suspended", "revoked"]);
const BLOCKED_SUBSCRIPTION_STATUSES = new Set([
  "cancelled",
  "canceled",
  "suspended",
  "revoked",
]);

const ADMIN_USER_ROLES = new Set([
  "owner",
  "admin",
  "companyadmin",
  "superadmin",
  "platformadmin",
]);

const ADMIN_COMPANY_ROLES = new Set(["owner", "admin", "companyadmin"]);
const ADMIN_ACCESS_LEVELS = new Set(["owner", "admin"]);

function normalizeValue(value) {
  return String(value || "")
    .trim()
    .replace(/[-_\s]/g, "")
    .toLowerCase();
}

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function listIncludesUid(list, uid) {
  return Array.isArray(list) && list.includes(uid);
}

function getContextCompanyRole(auth, uid) {
  const companyData =
    auth?.companyData ||
    auth?.company ||
    auth?.currentCompany ||
    auth?.activeCompany ||
    auth?.userData?.companyData ||
    null;

  if (!companyData || !uid) return "";

  if (companyData.ownerUid === uid || listIncludesUid(companyData.ownerUids, uid)) {
    return "owner";
  }

  if (listIncludesUid(companyData.adminUids, uid)) {
    return "admin";
  }

  return companyData.roles?.[uid] || "";
}

export function hasExplicitAdminAccess(auth) {
  const { currentUser, userData } = auth;
  const userRole = normalizeValue(userData?.role);
  const companyRole = normalizeValue(userData?.companyRole);
  const accessLevel = normalizeValue(userData?.accessLevel);
  const contextCompanyRole = normalizeValue(
    getContextCompanyRole(auth, currentUser?.uid)
  );

  return (
    ADMIN_USER_ROLES.has(userRole) ||
    ADMIN_COMPANY_ROLES.has(companyRole) ||
    ADMIN_ACCESS_LEVELS.has(accessLevel) ||
    userData?.isAdmin === true ||
    ADMIN_COMPANY_ROLES.has(contextCompanyRole)
  );
}

export function isAdminStatusBlocked(userData) {
  return (
    BLOCKED_ACCESS_STATUSES.has(normalizeStatus(userData?.accessStatus)) ||
    BLOCKED_SUBSCRIPTION_STATUSES.has(normalizeStatus(userData?.subscriptionStatus))
  );
}

function AdminAccessDenied() {
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <SBSLogoMark alt="" style={badgeStyle} />
        <h1 style={titleStyle}>Admin access required</h1>
        <p style={textStyle}>
          You do not have permission to open this administration area.
        </p>
        <div style={actionRowStyle}>
          <Link to="/operations" style={primaryLinkStyle}>
            Back to Operations
          </Link>
          <Link to="/" style={secondaryLinkStyle}>
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminRoute({ children }) {
  const auth = useAuth() || {};
  const { currentUser, userData, loading } = auth;

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Loading...</h1>
          <p style={textStyle}>Checking administrator access.</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  if (!userData || isAdminStatusBlocked(userData) || !hasExplicitAdminAccess(auth)) {
    return <AdminAccessDenied />;
  }

  return children;
}

const pageStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f7f4ec",
  padding: "20px",
};

const cardStyle = {
  width: "min(460px, 100%)",
  background: "#ffffff",
  border: "1px solid rgba(212, 175, 55, 0.45)",
  borderRadius: "14px",
  padding: "32px",
  textAlign: "center",
  boxShadow: "0 18px 50px rgba(24, 23, 23, 0.12)",
};

const badgeStyle = {
  width: "64px",
  height: "64px",
  margin: "0 auto 16px",
  borderRadius: "14px",
  objectFit: "contain",
  display: "block",
};

const titleStyle = {
  margin: "0 0 10px",
  color: "#181717",
  fontSize: "24px",
  fontWeight: "900",
};

const textStyle = {
  color: "#374151",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionRowStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "18px",
};

const primaryLinkStyle = {
  background: "#181717",
  color: "#fffbe8",
  border: "1px solid #d4af37",
  borderRadius: "10px",
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: "800",
  textDecoration: "none",
};

const secondaryLinkStyle = {
  ...primaryLinkStyle,
  background: "#ffffff",
  color: "#181717",
};
